import type { IndicadoresMunicipio, ValoresMunicipio } from "@/types/models/indicadores";

export type TendenciaTipo = "up" | "down" | "flat";
export type ValoresByIndicador = Record<number, ValoresMunicipio[]>;

export const formatIndicadorNumber = (value: number) =>
  new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(value);

export const normalizeValores = (valores: ValoresMunicipio[] = []) =>
  [...valores].sort((a, b) => a.ts.localeCompare(b.ts));

export function computeTrendFromValores(valores: ValoresMunicipio[]) {
  const sorted = normalizeValores(valores);
  const last = sorted.at(-1);
  const prev = sorted.at(-2);

  const pct = last && prev && prev.valor !== 0 ? ((last.valor - prev.valor) / Math.abs(prev.valor)) * 100 : 0;
  const trend: TendenciaTipo = pct > 0.01 ? "up" : pct < -0.01 ? "down" : "flat";

  return { sorted, last, prev, pct, trend };
}

export const groupValoresByIndicador = (valores: ValoresMunicipio[]): ValoresByIndicador =>
  valores.reduce<ValoresByIndicador>((acc, valor) => {
    const indicadorId = Number(valor.indicador);
    acc[indicadorId] = [...(acc[indicadorId] ?? []), valor];
    return acc;
  }, {});

export function getIndicadoresStats(indicadores: IndicadoresMunicipio[], valoresByIndicador: ValoresByIndicador) {
  let up = 0;
  let down = 0;
  let flat = 0;

  for (const indicador of indicadores) {
    const { trend } = computeTrendFromValores(valoresByIndicador[Number(indicador.id)] ?? []);

    if (trend === "up") up++;
    else if (trend === "down") down++;
    else flat++;
  }

  const categorias = new Set(indicadores.map((i) => i.categoria).filter((c): c is string => Boolean(c?.trim())));

  return { total: indicadores.length, categorias: categorias.size, up, down, flat };
}

export function getValoresStats(valores: ValoresMunicipio[]) {
  const nums = valores.map((v) => Number(v.valor)).filter((n) => !Number.isNaN(n));

  if (!nums.length) return { min: null, max: null, mean: null };

  return {
    min: Math.min(...nums),
    max: Math.max(...nums),
    mean: nums.reduce((a, b) => a + b, 0) / nums.length,
  };
}